/**
 * Common Map Utilities for VerveStacks Dashboard
 * 
 * This utility provides consistent map styling and configuration across all maps
 * in the dashboard, ensuring a clean, professional appearance that highlights
 * renewable energy data without visual clutter.
 */

import L from 'leaflet';

const CARTO_API_KEY = (process.env.REACT_APP_CARTO_API_KEY || '').trim();

/**
 * Append the CARTO raster basemap API key when one is configured.
 * Requests without a key are served with an "API key required" watermark.
 */
export const withCartoApiKey = (url) => {
  if (!url || !CARTO_API_KEY) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}key=${encodeURIComponent(CARTO_API_KEY)}`;
};

/**
 * Map Style Presets
 * Different styles optimized for different types of data visualization
 */
export const MAP_STYLES = {
  // Clean minimal style for renewable energy zones
  CLEAN_MINIMAL: {
    name: 'Clean Minimal',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    description: 'Clean style with clear country boundaries, perfect for renewable energy zones'
  },
  
  // Light style with subtle features
  LIGHT_SUBTLE: {
    name: 'Light Subtle',
    url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    description: 'Light background with minimal labels, ideal for data visualization'
  },
  
  // Dark theme for contrast
  DARK_THEME: {
    name: 'Dark Theme',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    description: 'Dark theme for high contrast with bright renewable energy markers'
  },
  
  // High contrast for accessibility
  HIGH_CONTRAST: {
    name: 'High Contrast',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    description: 'High contrast style for better accessibility and marker visibility'
  },
  
  // Terrain style for geographical context
  TERRAIN: {
    name: 'Terrain',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    description: 'Terrain style showing geographical features relevant to renewable energy'
  }
};

/**
 * Default map configuration optimized for renewable energy visualization
 */
export const DEFAULT_MAP_CONFIG = {
  // Map options (gestures managed by attachIntentionalMapInteraction when enabled)
  zoomControl: true,
  attributionControl: true,
  scrollWheelZoom: false,
  doubleClickZoom: true,
  dragging: false,
  boxZoom: true,
  keyboard: true,
  touchZoom: false,
  
  // Zoom limits
  minZoom: 2,
  maxZoom: 18,
  
  // Default style
  defaultStyle: MAP_STYLES.CLEAN_MINIMAL,
  
  // Marker defaults
  markerDefaults: {
    opacity: 0.7,
    fillOpacity: 0.6,
    weight: 1,
    color: 'white'
  }
};

/**
 * Create a standardized tile layer with consistent styling
 * @param {string} style - Style preset name from MAP_STYLES
 * @param {Object} options - Additional Leaflet tile layer options
 * @returns {L.TileLayer} Configured tile layer
 */
export const createTileLayer = (style = 'CLEAN_MINIMAL', options = {}) => {
  const styleConfig = (style && typeof style === 'object' && style.url)
    ? style
    : (MAP_STYLES[style] || MAP_STYLES.CLEAN_MINIMAL);
  
  const defaultOptions = {
    attribution: styleConfig.attribution,
    maxZoom: 18,
    detectRetina: true,
    ...options
  };
  
  return L.tileLayer(withCartoApiKey(styleConfig.url), defaultOptions);
};

/**
 * Create a map with standardized configuration
 * @param {HTMLElement} container - Map container element
 * @param {Object} options - Map initialization options
 * @returns {L.Map} Configured map instance
 */
export const createMap = (container, options = {}) => {
  const config = {
    ...DEFAULT_MAP_CONFIG,
    ...options
  };
  
  const map = L.map(container, {
    zoomControl: config.zoomControl,
    attributionControl: config.attributionControl,
    scrollWheelZoom: config.scrollWheelZoom,
    doubleClickZoom: config.doubleClickZoom,
    dragging: config.dragging,
    boxZoom: config.boxZoom,
    keyboard: config.keyboard,
    touchZoom: config.touchZoom,
    minZoom: config.minZoom,
    maxZoom: config.maxZoom
  });
  
  // Add default tile layer
  const tileLayer = createTileLayer(config.defaultStyle);
  tileLayer.addTo(map);
  
  if (config.enableResetControl !== false) {
    attachMapResetControl(map, {
      position: config.resetControlPosition || 'topright',
    });
  }

  if (config.enableIntentionalInteraction !== false) {
    attachIntentionalMapInteraction(map);
  }

  return map;
};

/**
 * Create a standardized circle marker
 * @param {Array} position - [lat, lng] coordinates
 * @param {Object} options - Marker options
 * @returns {L.CircleMarker} Configured circle marker
 */
export const createCircleMarker = (position, options = {}) => {
  const defaultOptions = {
    ...DEFAULT_MAP_CONFIG.markerDefaults,
    ...options
  };
  
  return L.circleMarker(position, defaultOptions);
};

/**
 * Create a custom control for any map
 * @param {Object} options - Control configuration
 * @returns {L.Control} Custom control
 */
export const createCustomControl = (options = {}) => {
  const CustomControl = L.Control.extend({
    onAdd: function(map) {
      const div = L.DomUtil.create('div', 'leaflet-custom-control');
      div.style.cssText = `
        background: white;
        border: 1px solid #ccc;
        border-radius: 8px;
        padding: 12px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        font-family: Arial, sans-serif;
        font-size: 12px;
        ${options.customStyles || ''}
      `;
      
      if (options.content) {
        div.innerHTML = options.content;
      }
      
      return div;
    }
  });
  
  return new CustomControl({ 
    position: options.position || 'bottomright',
    ...options
  });
};

/**
 * Calculate optimal map bounds for a set of coordinates
 * @param {Array} coordinates - Array of [lat, lng] coordinates
 * @param {number} padding - Padding factor (0-1)
 * @returns {L.LatLngBounds} Calculated bounds
 */
export const calculateMapBounds = (coordinates, padding = 0.1) => {
  if (!coordinates || coordinates.length === 0) {
    return L.latLngBounds([[0, 0], [0, 0]]);
  }
  
  const lats = coordinates.map(coord => coord[0]);
  let lngs = coordinates.map(coord => coord[1]);
  
  let minLng = Math.min(...lngs);
  let maxLng = Math.max(...lngs);

  // Antimeridian-aware longitude span.
  // Countries whose territory crosses 180°/-180° (Russia, Fiji, NZ, Kiribati,
  // USA-Aleutians tip, ...) report longitudes both near +180 and near -180.
  // A naive min/max produces a ~360° box that forces fitBounds to a
  // world view. If the raw span exceeds 180° we treat it as an
  // antimeridian crossing and shift any negative longitudes by +360 so the
  // values become continuous (e.g. Russia: 20..179 plus -179..-170 → 20..190).
  if (maxLng - minLng > 180) {
    lngs = lngs.map((lng) => (lng < 0 ? lng + 360 : lng));
    minLng = Math.min(...lngs);
    maxLng = Math.max(...lngs);
  }

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  
  const latPadding = (maxLat - minLat) * padding;
  const lngPadding = (maxLng - minLng) * padding;
  
  return L.latLngBounds(
    [minLat - latPadding, minLng - lngPadding],
    [maxLat + latPadding, maxLng + lngPadding]
  );
};

const RESET_VIEW_TOLERANCE = {
  lat: 1e-4,
  lng: 1e-4,
  zoom: 0.08,
};

const RESET_ICON_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
    <path d="M21 3v5h-5"/>
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
    <path d="M3 21v-5h5"/>
  </svg>`;

/**
 * Attach a reset control that restores the map to the last "home" view (after fitBounds).
 * Button is hidden until the user pans/zooms away from that view.
 *
 * @param {L.Map} map
 * @param {Object} options
 * @returns {Object} reset API (also on map._vsMapReset)
 */
export const attachMapResetControl = (map, options = {}) => {
  if (!map) {
    throw new Error('attachMapResetControl: map is required');
  }
  if (map._vsMapReset) {
    return map._vsMapReset;
  }

  const { position = 'topright', label = 'Reset view' } = options;

  const state = {
    homeCenter: null,
    homeZoom: null,
    programmatic: false,
    buttonEl: null,
    onMoveEnd: null,
  };

  const setButtonVisible = (visible) => {
    if (!state.buttonEl) return;
    state.buttonEl.classList.toggle('vs-map-reset-btn--visible', visible);
    state.buttonEl.setAttribute('aria-hidden', visible ? 'false' : 'true');
  };

  const isAtHome = () => {
    if (state.homeCenter == null || state.homeZoom == null) return true;
    const center = map.getCenter();
    const zoom = map.getZoom();
    return (
      Math.abs(center.lat - state.homeCenter.lat) < RESET_VIEW_TOLERANCE.lat &&
      Math.abs(center.lng - state.homeCenter.lng) < RESET_VIEW_TOLERANCE.lng &&
      Math.abs(zoom - state.homeZoom) < RESET_VIEW_TOLERANCE.zoom
    );
  };

  const updateButtonVisibility = () => {
    if (state.programmatic || state.homeCenter == null) {
      setButtonVisible(false);
      return;
    }
    setButtonVisible(!isAtHome());
  };

  const captureHomeView = () => {
    const center = map.getCenter();
    state.homeCenter = L.latLng(center.lat, center.lng);
    state.homeZoom = map.getZoom();
    setButtonVisible(false);
  };

  const finishProgrammaticMove = () => {
    state.programmatic = false;
    captureHomeView();
  };

  const resetToHome = () => {
    if (state.homeCenter == null || state.homeZoom == null) return;
    state.programmatic = true;
    setButtonVisible(false);
    map.setView(state.homeCenter, state.homeZoom, { animate: true });
    map.once('moveend', () => {
      finishProgrammaticMove();
    });
  };

  const setHomeByFitBounds = (bounds, fitOptions = {}) => {
    if (!bounds || !bounds.isValid || !bounds.isValid()) {
      return;
    }
    state.programmatic = true;
    setButtonVisible(false);

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      map.off('moveend', settle);
      finishProgrammaticMove();
    };

    map.once('moveend', settle);
    try {
      map.fitBounds(bounds, fitOptions);
    } catch (err) {
      map.off('moveend', settle);
      state.programmatic = false;
      throw err;
    }

    // fitBounds may not fire moveend if the view is unchanged
    setTimeout(() => {
      if (!settled) settle();
    }, 150);
  };

  const ResetControl = L.Control.extend({
    onAdd() {
      const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control vs-map-reset-control');
      const button = L.DomUtil.create('button', 'map-control-btn vs-map-reset-btn', container);
      button.type = 'button';
      button.setAttribute('aria-label', label);
      button.title = label;
      button.innerHTML = `${RESET_ICON_SVG}<span class="vs-map-reset-label">Reset</span>`;
      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.on(button, 'click', (e) => {
        L.DomEvent.stop(e);
        resetToHome();
      });
      state.buttonEl = button;
      setButtonVisible(false);
      return container;
    },
  });

  const control = new ResetControl({ position });
  control.addTo(map);

  state.onMoveEnd = () => {
    if (state.programmatic) return;
    updateButtonVisibility();
  };
  map.on('moveend', state.onMoveEnd);

  const api = {
    captureHomeView,
    setHomeByFitBounds,
    resetToHome,
    hasHome: () => state.homeCenter != null,
    destroy: () => {
      map.off('moveend', state.onMoveEnd);
      if (control && map) {
        map.removeControl(control);
      }
      state.buttonEl = null;
      delete map._vsMapReset;
    },
  };

  map._vsMapReset = api;
  return api;
};

function isCoarsePointerDevice() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

function isMapControlTarget(target) {
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(target.closest('.leaflet-control'));
}

/**
 * Passive-by-default map gestures so page scroll is not hijacked.
 * Desktop: page scroll over map; Ctrl/Cmd + wheel zooms; click map to enter interactive mode.
 * Touch: one-finger scrolls page (touch-action: pan-y); two-finger or tap enables pan/zoom.
 *
 * @param {L.Map} map
 * @param {Object} options
 * @returns {{ setActive: Function, setPassive: Function, destroy: Function, isActive: Function }}
 */
export const attachIntentionalMapInteraction = (map, options = {}) => {
  if (!map) {
    throw new Error('attachIntentionalMapInteraction: map is required');
  }
  if (map._vsMapInteraction) {
    return map._vsMapInteraction;
  }

  const container = map.getContainer();
  let active = false;

  const applyPassiveGestures = () => {
    map.scrollWheelZoom.disable();
    map.dragging.disable();
    map.touchZoom.disable();
    map.doubleClickZoom.disable();
    if (map.boxZoom) map.boxZoom.disable();
    if (map.keyboard) map.keyboard.disable();
    container.classList.remove('vs-map-interactive');
    container.classList.add('vs-map-passive');
    container.style.touchAction = 'pan-y';
  };

  const applyActiveGestures = () => {
    map.dragging.enable();
    map.touchZoom.enable();
    map.doubleClickZoom.enable();
    if (map.boxZoom) map.boxZoom.enable();
    if (map.keyboard) map.keyboard.enable();
    if (!isCoarsePointerDevice()) {
      map.scrollWheelZoom.enable();
    } else {
      map.scrollWheelZoom.disable();
    }
    container.classList.add('vs-map-interactive');
    container.classList.remove('vs-map-passive');
    container.style.touchAction = 'none';
  };

  const setPassive = () => {
    active = false;
    applyPassiveGestures();
  };

  const setActive = () => {
    active = true;
    applyActiveGestures();
  };

  applyPassiveGestures();

  const onWheel = (e) => {
    if (active && !isCoarsePointerDevice()) {
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      map.scrollWheelZoom.enable();
    } else {
      map.scrollWheelZoom.disable();
    }
  };

  const onWheelCapture = (e) => {
    if (active) return;
    if (!(e.ctrlKey || e.metaKey)) return;
    if (!map.scrollWheelZoom.enabled()) {
      map.scrollWheelZoom.enable();
    }
  };

  let touchTapStartX = 0;
  let touchTapStartY = 0;
  let touchTapTracking = false;

  const onMapPointerDown = (e) => {
    if (isMapControlTarget(e.target)) return;
    if (active) return;
    setActive();
  };

  const onDocumentPointerDown = (e) => {
    if (!active) return;
    if (container.contains(e.target)) return;
    setPassive();
  };

  const onTouchStart = (e) => {
    if (e.touches && e.touches.length >= 2) {
      touchTapTracking = false;
      setActive();
      return;
    }
    if (e.touches && e.touches.length === 1 && !active) {
      touchTapStartX = e.touches[0].clientX;
      touchTapStartY = e.touches[0].clientY;
      touchTapTracking = true;
    }
  };

  const onTouchEnd = (e) => {
    if (active || !touchTapTracking) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - touchTapStartX);
    const dy = Math.abs(t.clientY - touchTapStartY);
    touchTapTracking = false;
    if (dx < 12 && dy < 12) {
      setActive();
    }
  };

  container.addEventListener('wheel', onWheel, { passive: true });
  container.addEventListener('wheel', onWheelCapture, { passive: true, capture: true });
  container.addEventListener('mousedown', onMapPointerDown);
  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchend', onTouchEnd, { passive: true });
  container.addEventListener('touchcancel', onTouchEnd, { passive: true });
  document.addEventListener('mousedown', onDocumentPointerDown);
  document.addEventListener('touchstart', onDocumentPointerDown, { passive: true });

  const api = {
    setActive,
    setPassive,
    isActive: () => active,
    destroy: () => {
      setPassive();
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('wheel', onWheelCapture, true);
      container.removeEventListener('mousedown', onMapPointerDown);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
      document.removeEventListener('mousedown', onDocumentPointerDown);
      document.removeEventListener('touchstart', onDocumentPointerDown);
      container.classList.remove('vs-map-passive', 'vs-map-interactive');
      container.style.touchAction = '';
      delete map._vsMapInteraction;
    },
  };

  map._vsMapInteraction = api;
  return api;
};

/**
 * Fit bounds and store the resulting view as home (shows reset after user moves map).
 */
export const fitMapBoundsAsHome = (map, bounds, fitOptions = {}) => {
  const reset = map._vsMapReset || attachMapResetControl(map);
  reset.setHomeByFitBounds(bounds, fitOptions);
};

const mapUtils = {
  MAP_STYLES,
  DEFAULT_MAP_CONFIG,
  withCartoApiKey,
  createTileLayer,
  createMap,
  createCircleMarker,
  createCustomControl,
  calculateMapBounds,
  attachMapResetControl,
  attachIntentionalMapInteraction,
  fitMapBoundsAsHome,
};

export default mapUtils;
